/* eslint-disable local/ensure-wac-token-usage */
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState
} from "react";

import { css } from "@compiled/react";
import { injectIntl, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    ufoEventConfirmationPageTemplateSelectionBack,
    ufoEventConfirmationPageTemplateSelectionContinue
} from "@/components/signup/utils";
import {
    TRACKING_EVENT_ACTION_SUBJECT_IDS,
    USER_SEARCH_PARAMS
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";
import { experiments, getExperimentWithExposures } from "@/utils/experiment";

import {
    JiraTemplateChoice,
    useGupsStatus,
    useJiraTemplateChoice
} from "../../hooks";
import { useConfirmationPage } from "../../hooks/use-confirmation-page";
import { useScreenManager } from "../../hooks/use-screen-manager";
import {
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    UserSegmentationTeamTypes
} from "../../models/user-segmentation";
import { getCurrentUrlWithParams } from "../../utils/utils";
import { JiraOnboardingContainer } from "../jira-onboarding-container/jira-onboarding-container";

import Body from "./components/body";
import { getTeamTypeTemplateOptions } from "./team-type-template-map";
import { TemplateOption, TemplateOptionMetadata } from "./template-definitions";

const buttonWrapperStyleDefault = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    margin: "0 auto",
    padding: "36px 20px 20px 20px",
    border: "none",
    gap: token("space.250"),
    width: "372px",
    [media.below.sm]: {
        position: "fixed",
        insetInlineStart: 0,
        insetBlockEnd: "0",
        backgroundColor: "#FFF",
        padding: "14px 18px",
        width: "100%",
        zIndex: 1000,
        boxShadow: "-1px 0px 2px 0px #00000040"
    }
});

const buttonCssOverrideDefault = css({
    borderRadius: "40px !important",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "0 24px !important",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontSize: "16px"
    },
    [media.above.sm]: {
        width: "min-content !important"
    }
});

const backButtonOverrideDefault = css({
    border: "2px solid #101214 !important",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        [media.below.sm]: {
            position: "relative",
            insetBlockStart: "-2px"
        }
    },
    [media.above.sm]: {
        lineHeight: "33px !important"
    }
});

const getMessages = (
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
) => ({
    header: formatMessage({
        id: "TransparentTemplateChoice.header.latest",
        defaultMessage: "Select a template to get started"
    }),
    subheader: formatMessage({
        id: "TransparentTemplateChoice.subheader.latest",
        defaultMessage: "Your choice won't limit what you can do in Jira"
    }),
    back: formatMessage({
        id: "TransparentTemplateChoice.backButton",
        defaultMessage: "Back"
    }),
    continue: formatMessage({
        id: "TransparentTemplateChoice.continueButton",
        defaultMessage: "Continue"
    }),
    transparentTemplateV2subHeader: formatMessage({
        id: "TransparentTemplateV2.subheader",
        defaultMessage:
            "You can always change this later. Selecting a template won’t limit what you can do."
    })
});

export const TemplateChoiceViewBase = ({
    contextSelectedTeamTypeState
}: {
    contextSelectedTeamTypeState: UserSegmentationTeamTypes;
}) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { addTemplateCallback } = useGupsStatus();

    //set these on submit
    const {
        setTemplateChoice,
        setTemplateChoiceId,
        setTemplateChoiceName,
        goBackToUserSegmentation,
        setTemplateMetadata
    } = useJiraTemplateChoice();

    const { prevScreen, nextScreen, prevScreenExists, skipScreen } =
        useScreenManager();

    const {
        userSegmentationContext: { selectedTeamType: rawSelectedTeamType }
    } = useConfirmationPage();

    const selectedTeamType = contextSelectedTeamTypeState;

    const doesPreviousScreenExist = prevScreenExists && prevScreenExists();

    const removeUrlParameter = useCallback((url: string, paramKey: string) => {
        const r = new URL(url);
        r.searchParams.delete(paramKey);
        return r.href;
    }, []);

    //set these on select
    const [selectedTemplate, setSelectedTemplate] =
        useState<JiraTemplateChoice>();
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
    const [selectedCardTitle, setSelectedCardTitle] = useState<string>();
    const [selectedTemplateMetadata, setSelectedTemplateMetadata] =
        useState<TemplateOptionMetadata>();

    const MADLIB_SKIP_TEAM_TYPES: UserSegmentationTeamTypes[] = [
        USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
        USER_SEGMENTATION_TEAM_TYPE_MARKETING,
        USER_SEGMENTATION_TEAM_TYPE_HR,
        USER_SEGMENTATION_TEAM_TYPE_LEGAL,
        USER_SEGMENTATION_TEAM_TYPE_FINANCE
    ];

    // get template choices based on team type
    const teamTemplateChoices: TemplateOption[] = useMemo(
        () => getTeamTypeTemplateOptions(selectedTeamType, intl.formatMessage),
        [intl.formatMessage, selectedTeamType]
    );

    useLayoutEffect(() => {
        // Ensure team type param is set in the URL before experiment exposure.
        // This allows for proper enrollment into the experiment for existing Jira users
        // that do not set their team type on the previous screen and
        // instead, it is resolved from a fetch request to their ID profile.
        const url = new URL(window.location.href);
        if (!rawSelectedTeamType) {
            return;
        }
        if (!url.searchParams.get("team_type")) {
            url.searchParams.set("team_type", rawSelectedTeamType);
            history.replaceState(null, "", url.toString());
        }

        if (MADLIB_SKIP_TEAM_TYPES.includes(rawSelectedTeamType)) {
            const madLibExperiment = getExperimentWithExposures(
                experiments.jiraOnboardingAiMadlibV2
            );
            const enrolledInMadLibTreatment =
                madLibExperiment.type === "enrolled" && madLibExperiment.value;

            if (enrolledInMadLibTreatment) {
                skipScreen();
            }
        }
    }, [skipScreen, rawSelectedTeamType]);

    //set selected template to first template in array
    useEffect(() => {
        if (teamTemplateChoices) {
            setSelectedTemplateId(teamTemplateChoices[0]?.option.templateId);
            setSelectedTemplate(teamTemplateChoices[0]?.option.templateType);
            setSelectedTemplateMetadata(teamTemplateChoices[0]?.metadata);
            setSelectedCardTitle(teamTemplateChoices[0]?.option.title);
        }
    }, [teamTemplateChoices]);

    const handleBack = useCallback(() => {
        try {
            ufoEventConfirmationPageTemplateSelectionBack.start();
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "button",
                actionSubjectId:
                    TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceBack,
                attributes: {
                    atlasTicket: "ATLAS-69623"
                }
            }).fire();
            const updatedUrl = removeUrlParameter(
                window.location.href,
                USER_SEARCH_PARAMS.teamType
            );
            window.history.replaceState({}, "", updatedUrl);
            goBackToUserSegmentation();
            prevScreen();
            ufoEventConfirmationPageTemplateSelectionBack.success();
        } catch (e) {
            ufoEventConfirmationPageTemplateSelectionBack.failure({
                error: e
            });
        }
    }, [
        createAnalyticsEvent,
        goBackToUserSegmentation,
        prevScreen,
        removeUrlParameter
    ]);

    const handleContinue = useCallback(async () => {
        try {
            ufoEventConfirmationPageTemplateSelectionContinue.start();
            ufoEventConfirmationPageTemplateSelectionContinue.setCustomData({
                teamType: selectedTeamType,
                template: selectedTemplate,
                templateId: selectedTemplateId
            });
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "button",
                actionSubjectId:
                    TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceStartNow,
                attributes: {
                    atlasTicket: "ATLAS-69623",
                    template: selectedTemplate,
                    teamType: selectedTeamType,
                    templateId: selectedTemplateId
                }
            }).fire();

            // add templateChoice to query param to maintain selection on refresh
            const updatedUrl = getCurrentUrlWithParams({
                templateId: selectedTemplateId || "",
                templateChoice: selectedTemplate || "",
                jtbd: selectedTemplateMetadata?.jtbd || ""
            });
            window.history.replaceState({}, "", updatedUrl);

            if (selectedTemplateId) {
                await addTemplateCallback(selectedTemplateId);
            }

            setTemplateMetadata(selectedTemplateMetadata);
            setTemplateChoice(selectedTemplate);
            setTemplateChoiceId(selectedTemplateId);
            setTemplateChoiceName(selectedCardTitle);

            nextScreen();
            ufoEventConfirmationPageTemplateSelectionContinue.success();
        } catch (e) {
            ufoEventConfirmationPageTemplateSelectionContinue.failure({
                error: e
            });
        }
    }, [
        addTemplateCallback,
        createAnalyticsEvent,
        nextScreen,
        selectedCardTitle,
        selectedTeamType,
        selectedTemplate,
        selectedTemplateId,
        selectedTemplateMetadata,
        setTemplateChoice,
        setTemplateChoiceId,
        setTemplateChoiceName,
        setTemplateMetadata
    ]);

    const messages = useMemo(
        () => getMessages(intl.formatMessage),
        [intl.formatMessage]
    );

    return (
        <JiraOnboardingContainer
            header={messages.header}
            subheader={messages.transparentTemplateV2subHeader}
            hideFooterOnMobile={true}
        >
            <Body
                setSelectedTemplate={setSelectedTemplate}
                setSelectedTemplateId={setSelectedTemplateId}
                setSelectedCardTitle={setSelectedCardTitle}
                setSelectedTemplateMetadata={setSelectedTemplateMetadata}
                selectedCardTitle={selectedCardTitle}
                cards={teamTemplateChoices}
                teamType={selectedTeamType}
            />
            <div css={buttonWrapperStyleDefault}>
                {doesPreviousScreenExist && (
                    <Button
                        shouldFitContainer
                        appearance="subtle"
                        css={[
                            buttonCssOverrideDefault,
                            backButtonOverrideDefault
                        ]}
                        onClick={handleBack}
                    >
                        {messages.back}
                    </Button>
                )}
                <Button
                    css={buttonCssOverrideDefault}
                    shouldFitContainer
                    appearance="primary"
                    onClick={handleContinue}
                >
                    {messages.continue}
                </Button>
            </div>
        </JiraOnboardingContainer>
    );
};

export const TemplateChoiceView = injectIntl(() => {
    //screen analytics event here
    const {
        userSegmentationContext: {
            selectedTeamType:
                contextSelectedTeamTypeState = "software-development"
        }
    } = useConfirmationPage();

    return (
        <TemplateChoiceViewBase
            contextSelectedTeamTypeState={contextSelectedTeamTypeState}
        />
    );
});
